import {
  RouteManagementRow,
  RouteType,
  RouteUsageCounts,
} from "../../types/route.type";
import { Branch } from "../../types/user.type";

export const ROUTE_MANAGEMENT_PATH = "/routes";
export const ROUTE_MENU_TITLE = "สายส่ง";

export const ROUTE_TYPE_LABEL: Record<RouteType, string> = {
  MAIN: "สายปกติ",
  SPECIAL: "สายพิเศษ",
};

export const TEST_MODE_LABEL = "โหมดทดสอบ";
export const LIVE_MODE_LABEL = "ใช้งานจริง";
export const QR_PAYMENT_LABEL = "QR";

/** The confirm dialog's wording for one direction of the QR switch. */
export interface QrPaymentSwitchText {
  title: string;
  description: string;
  confirmLabel: string;
  successMsg: string;
}

export const QR_PAYMENT_ON_TEXT: QrPaymentSwitchText = {
  title: "เปิดรับชำระด้วย QR",
  description:
    "ลูกค้าของสายนี้จะเห็นตัวเลือกชำระเงินด้วย QR ตั้งแต่บิลถัดไป",
  confirmLabel: "เปิด QR",
  successMsg: "เปิดรับชำระด้วย QR สำเร็จ",
};

export const QR_PAYMENT_OFF_TEXT: QrPaymentSwitchText = {
  title: "ปิดรับชำระด้วย QR",
  description:
    "ลูกค้าของสายนี้จะไม่เห็นตัวเลือกชำระเงินด้วย QR ตั้งแต่บิลถัดไป",
  confirmLabel: "ปิด QR",
  successMsg: "ปิดรับชำระด้วย QR สำเร็จ",
};

/** Going back to test mode also switches QR off on the server, and going live does not restore it. */
export const QR_OFF_WITH_TEST_MODE_NOTE =
  "การรับชำระด้วย QR ของสายนี้จะถูกปิดด้วย ต้องเปิดใหม่เองเมื่อกลับมาใช้งานจริง";

const QR_NEEDS_LIVE_ROUTE_TOOLTIP = "ต้องเปิดใช้งานสายจริงก่อน";

/** Switching QR off is always possible; switching it on needs the server's go-ahead. */
export const canSwitchQrPayment = (route: RouteManagementRow): boolean =>
  route.qr_payment_enabled || route.qr_payment_can_enable;

/** What the QR button does on this route, or why it cannot. */
export const readQrSwitchTooltip = (route: RouteManagementRow): string => {
  if (route.qr_payment_enabled) return QR_PAYMENT_OFF_TEXT.title;
  if (route.qr_payment_can_enable) return QR_PAYMENT_ON_TEXT.title;
  return QR_NEEDS_LIVE_ROUTE_TOOLTIP;
};

// Mirrors the API: route_name is VARCHAR(100) and needs at least 3 characters.
const ROUTE_NAME_MIN_LENGTH = 3;
export const ROUTE_NAME_MAX_LENGTH = 100;

// Route numbers are shown two digits wide, as they appear inside route codes ("PK07").
const ROUTE_SEQ_DIGITS = 2;

export const formatRouteSeq = (routeSeq: number): string =>
  String(routeSeq).padStart(ROUTE_SEQ_DIGITS, "0");

/** The typed name's problem, or null when it can be sent. */
export const readRouteNameError = (name: string): string | null => {
  const trimmedLength = name.trim().length;
  if (trimmedLength === 0) return "กรุณากรอกชื่อสาย";
  if (trimmedLength < ROUTE_NAME_MIN_LENGTH)
    return `ชื่อสายต้องมีอย่างน้อย ${ROUTE_NAME_MIN_LENGTH} ตัวอักษร`;
  if (trimmedLength > ROUTE_NAME_MAX_LENGTH)
    return `ชื่อสายยาวได้ไม่เกิน ${ROUTE_NAME_MAX_LENGTH} ตัวอักษร`;
  return null;
};

export const isRouteInUse = (usage: RouteUsageCounts): boolean =>
  usage.live_store_count > 0 || usage.active_staff_count > 0;

/** "ร้านค้า 120 ร้าน และพนักงานขาย 1 คน" — only the parts that are not zero. */
export const describeRouteUsage = (usage: RouteUsageCounts): string => {
  const parts: string[] = [];
  if (usage.live_store_count > 0)
    parts.push(`ร้านค้า ${usage.live_store_count} ร้าน`);
  if (usage.active_staff_count > 0)
    parts.push(`พนักงานขาย ${usage.active_staff_count} คน`);
  return parts.join(" และ");
};

/** Why a route cannot be deleted — shared by the disabled button and the 409 refusal. */
export const buildRouteInUseMessage = (
  routeCode: string,
  usage: RouteUsageCounts,
): string =>
  `ลบสาย ${routeCode} ไม่ได้ เพราะยังมี${describeRouteUsage(usage)}ผูกกับสายนี้อยู่`;

/**
 * The branch an all-branch user lands on: their own token branch when it is in the list,
 * otherwise the first branch.
 */
export const pickDefaultBranchUuid = (
  branches: Branch[],
  tokenBranchCode: string | null,
): string => {
  const ownBranch = branches.find(
    (branch) => branch.branch_code === tokenBranchCode,
  );
  return (ownBranch ?? branches[0])?.uuid ?? "";
};

export interface RouteSummary {
  mainCount: number;
  testModeCount: number;
  specialCount: number;
}

export const summarizeRoutes = (rows: RouteManagementRow[]): RouteSummary => {
  const mainRows = rows.filter((row) => row.route_type === "MAIN");
  return {
    mainCount: mainRows.length,
    testModeCount: mainRows.filter((row) => row.is_test_mode).length,
    specialCount: rows.length - mainRows.length,
  };
};
